import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, Phone, Mail, MapPin, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge, ConfirmDelete } from "@/components/common";
import { ClientDialog, InterventionDialog, OrderDialog, ReminderDialog } from "@/components/forms";
import { InterventionsTable, OrdersTable, RemindersList } from "@/components/tables";
import { api, formatApiError } from "@/lib/api";
import { useList } from "@/lib/hooks";
import { CLIENT_TYPES, EQUIPMENT_TYPES, CONTRACT_STATUS, fmtMin } from "@/lib/constants";
import { EquipmentDialog } from "@/components/v3forms";

const Overview = ({ id }) => {
  const [o, setO] = useState(null);
  const [add, setAdd] = useState(false);
  const load = useCallback(() => api.get(`/clients/${id}/overview`).then((r) => setO(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!o) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="client-equipment">
        <div className="mb-2 flex items-center justify-between"><h2 className="text-base font-semibold text-white">Équipements ({o.equipment.length})</h2>
          <Button size="sm" variant="secondary" onClick={() => setAdd(true)} data-testid="client-add-equipment"><Plus className="mr-1 h-4 w-4" />Équipement</Button></div>
        {o.equipment.map((e) => <Link key={e.id} to={`/equipements/${e.id}`} className="block truncate py-1 text-sm text-cyan-200 hover:underline">{e.name || `${e.brand} ${e.model}`} <span className="text-xs text-slate-400">· {EQUIPMENT_TYPES[e.type]}</span></Link>)}
        {!o.equipment.length && <p className="text-sm text-slate-400">Aucun équipement.</p>}
      </section>
      <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="client-contracts">
        <h2 className="mb-2 text-base font-semibold text-white">Contrats ({o.contracts.length})</h2>
        {o.contracts.map((c) => (
          <Link key={c.id} to={`/contrats/${c.id}`} className="flex flex-wrap items-center gap-2 py-1 text-sm text-cyan-200 hover:underline">{c.number} · {c.name}
            <Badge value={c.effective_status} map={CONTRACT_STATUS} />{c.current.available != null && <span className="text-xs text-slate-400">reste {fmtMin(c.current.remaining)}</span>}</Link>
        ))}
        {!o.contracts.length && <p className="text-sm text-slate-400">Aucun contrat.</p>}
      </section>
      <EquipmentDialog open={add} onOpenChange={setAdd} initial={{ client_id: id }} fixedClient onSaved={load} />
    </div>
  );
};

const Info = ({ icon: Icon, children, href }) =>
  children ? (
    <a href={href} className="flex items-center gap-2 text-sm text-slate-300 hover:text-cyan-300">
      <Icon className="h-4 w-4 text-cyan-300" />{children}
    </a>
  ) : null;

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [dialog, setDialog] = useState(null);
  const load = useCallback(() => api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => nav("/clients")), [id, nav]);
  useEffect(() => {
    load();
  }, [load]);
  const inter = useList("/interventions", { client_id: id });
  const orders = useList("/orders", { client_id: id });
  const rem = useList("/reminders", { client_id: id });
  const close = (o) => !o && setDialog(null);

  const remove = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client supprimé");
      nav("/clients");
    } catch (e) {
      setDialog(null);
      toast.error(formatApiError(e), { duration: 9000 });
    }
  };
  const toggleArchive = async () => {
    try {
      await api.patch(`/clients/${id}`, { archived: !client.archived });
      toast.success(client.archived ? "Client réactivé" : "Client archivé : historique conservé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!client) return null;
  const address = [client.address, [client.postal_code, client.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <>
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-cyan-300" data-testid="back-to-clients"><ArrowLeft className="h-4 w-4" />Clients</Link>
      <section className="dt-card flex flex-col gap-4 p-6 sm:flex-row sm:items-start sm:justify-between dt-rise" data-testid="client-detail-card">
        <div className="space-y-2">
          <Badge value={client.type} map={CLIENT_TYPES} />
          {client.archived && <span className="ml-2 rounded-full border border-slate-400/40 px-2 py-0.5 text-xs text-slate-300" data-testid="client-archived-badge">Archivé</span>}
          <h1 className="text-3xl font-bold text-white" data-testid="client-detail-name">{client.name}</h1>
          {client.company && <p className="text-slate-300">{client.company}</p>}
          <div className="flex flex-col gap-1 pt-2">
            <Info icon={Phone} href={`tel:${client.phone}`}>{client.phone}</Info>
            <Info icon={Mail} href={`mailto:${client.email}`}>{client.email}</Info>
            <Info icon={MapPin} href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}>{address}</Info>
          </div>
          {client.notes && <p className="max-w-xl whitespace-pre-line pt-2 text-sm text-slate-400">{client.notes}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setDialog("edit")} data-testid="client-edit-button"><Pencil className="mr-1 h-4 w-4" />Modifier</Button>
          <Button variant="secondary" onClick={toggleArchive} data-testid="client-archive-button">{client.archived ? "Réactiver" : "Archiver"}</Button>
          <Button variant="ghost" onClick={() => setDialog("delete")} data-testid="client-delete-button" className="hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </section>
      <Overview id={id} />

      <Tabs defaultValue="interventions">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="interventions" data-testid="client-tab-interventions">Historique ({inter.items.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="client-tab-orders">Commandes ({orders.items.length})</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="client-tab-reminders">Rappels ({rem.items.length})</TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setDialog("intervention")} data-testid="client-add-intervention"><Plus className="mr-1 h-4 w-4" />Intervention</Button>
            <Button size="sm" variant="secondary" onClick={() => setDialog("order")} data-testid="client-add-order"><Plus className="mr-1 h-4 w-4" />Commande</Button>
            <Button size="sm" variant="secondary" onClick={() => setDialog("reminder")} data-testid="client-add-reminder"><Plus className="mr-1 h-4 w-4" />Rappel</Button>
          </div>
        </div>
        <TabsContent value="interventions" className="mt-4"><InterventionsTable items={inter.items} reload={inter.reload} hideClient /></TabsContent>
        <TabsContent value="orders" className="mt-4"><OrdersTable items={orders.items} reload={orders.reload} hideClient /></TabsContent>
        <TabsContent value="reminders" className="mt-4"><RemindersList items={rem.items} reload={rem.reload} /></TabsContent>
      </Tabs>

      <ClientDialog open={dialog === "edit"} onOpenChange={close} initial={client} onSaved={load} />
      <InterventionDialog open={dialog === "intervention"} onOpenChange={close} initial={{ client_id: id }} onSaved={inter.reload} />
      <OrderDialog open={dialog === "order"} onOpenChange={close} initial={{ client_id: id }} onSaved={orders.reload} />
      <ReminderDialog open={dialog === "reminder"} onOpenChange={close} initial={{ client_id: id }} onSaved={rem.reload} />
      <ConfirmDelete open={dialog === "delete"} onOpenChange={close} onConfirm={remove} label="ce client (refusé s'il a des factures, contrats, bons signés ou documents : utilisez alors « Archiver »)" />
    </>
  );
}
